/* eslint-disable tsdoc/syntax */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { memo, useCallback, useEffect, useState } from "react";

import { motion } from "framer-motion";
import { FileRejection, useDropzone } from "react-dropzone";

import Check from "@zenith/assets/icons/Check";
import CheckCircle from "@zenith/assets/icons/CheckCircle";
import Circle from "@zenith/assets/icons/Circle";
import JpgFile from "@zenith/assets/icons/JpgFile";
import PdfFile from "@zenith/assets/icons/PdfFile";
import PngFile from "@zenith/assets/icons/PngFile";
import Trash from "@zenith/assets/icons/Trash";
import {
  FILE_INVALID_TYPE_SMILE_ID,
  FILE_SIZE_MAX_LIMIT,
  FILE_SIZE_MIN_LIMIT,
  FILE_SIZE_MIN_LIMIT_NATIONAL_ID,
  MAX_SIZE,
  MIN_SIZE_SMILE_ID,
} from "@zenith/data/constants";
import ImagePreviewComponent from "../../ImagePreviewComponent/image-preview-component/image-preview-component";
import SpinnerComponent from "../../SpinnerComponent/spinner-component/spinner-component";

/**
 * Props for Drop Zone Component.
 * @interface
 * @name DropZoneComponentProps
 * @kind interface
 * @exports
 */
export interface DropZoneComponentProps {
  /**
   * Name for the field.
   * @property
   * @name fieldName
   * @kind property
   * @memberof DropZoneComponentProps
   * @type {string}
   */
  fieldName: string;
  /**
   * Callback fired to get the image file.
   * @method
   * @name getImage
   * @kind property
   * @memberof DropZoneComponentProps
   * @param {File | null} image
   * @returns {void}
   */
  getImage: (image: File | null) => void;
  /**
   * Callback fired to delete the image file.
   * @method
   * @name deleteImage
   * @kind property
   * @memberof DropZoneComponentProps
   * @returns {void}
   */
  deleteImage: () => void;
  /**
   * Listener for the parent file change.
   * @property
   * @name parentFileChangeListener
   * @kind property
   * @memberof DropZoneComponentProps
   * @type {unknown}
   */
  parentFileChangeListener?: unknown;
  /**
   * (optional)
   * If `true`, disables the drop zone.
   * @property
   * @name disabled
   * @kind property
   * @memberof DropZoneComponentProps
   * @type {boolean | undefined}
   */
  disabled?: boolean;
  /**
   * (optional)
   * Image file.
   * @property
   * @name file
   * @kind property
   * @memberof DropZoneComponentProps
   * @type {File | undefined}
   */
  file?: File;
  /**
   * (optional)
   * Image file.
   * @default ["jpeg"]
   * @method
   * @name acceptedFileTypes
   * @kind property
   * @memberof DropZoneComponentProps
   */
  acceptedFileTypes?: ("jpeg" | "png" | "pdf")[];
  /**
   * (optional)
   * Accepted minimal size, in bytes.
   * @default 600_000 // 600KB
   * @property
   * @name acceptedMinSize
   * @kind property
   * @memberof DropZoneComponentProps
   * @type {number | undefined}
   */
  acceptedMinSize?: number;
  /**
   * (optional)
   * Error message for invalid file type.
   * @default "File type must be JPEG"
   * @property
   * @name fileInvalidTypeErrorMessage
   * @kind property
   * @memberof DropZoneComponentProps
   * @type {string | undefined}
   */
  fileInvalidTypeErrorMessage?: string;
  documentType?: string;
  dropZoneHeight?: string;
  hidePreview?: boolean;
  /**
   * (optional)
   * Text for the link that opens the file preview.
   * @default "Preview"
   * @property
   * @name previewLabel
   * @type {string | undefined}
   */
  previewLabel?: string;
  hideStatusIndicator?: boolean;
  /**
   * (optional)
   * Hides the file size in the accepted-file row — for a file whose real size
   * isn't known (e.g. a placeholder representing an already-uploaded file).
   * @property
   * @name hideSize
   * @type {boolean | undefined}
   */
  hideSize?: boolean;
  /**
   * (optional)
   * Forces the in-progress (spinner) row regardless of internal drop state —
   * for surfacing a real async upload that outlives the component's own
   * animation timing.
   * @property
   * @name isUploading
   * @type {boolean | undefined}
   */
  isUploading?: boolean;
  /**
   * (optional)
   * Outlines the uploaded file row in red, e.g. for a document a reviewer
   * rejected.
   * @property
   * @name hasError
   * @type {boolean | undefined}
   */
  hasError?: boolean;
}

/**
 * Memoized Drop Zone Component.
 * @constant
 * @name DropZoneComponent
 * @kind variable
 * @type {React.NamedExoticComponent<DropZoneComponentProps>}
 */
const DropZoneComponent: React.NamedExoticComponent<DropZoneComponentProps> =
  memo(
    /**
     * Drop Zone Component.
     * @function
     * @name DropZoneComponent
     * @kind function
     * @memberof DropZoneComponent
     * @param {DropZoneComponentProps} props Props for Drop Zone Component.
     * @returns {JSX.Element} Drop Zone Component.
     */
    function DropZoneComponent(props: DropZoneComponentProps): JSX.Element {
      const getFileIcon = (name: string) => {
        const ext = name.split(".").pop()?.toLowerCase() ?? "";
        if (ext === "pdf") return <PdfFile />;
        if (ext === "jpg" || ext === "jpeg") return <JpgFile />;
        return <PngFile />;
      };

      const formatFileSize = (bytes: number): string => {
        if (bytes >= 1024 * 1024)
          return `${(bytes / (1024 * 1024)).toFixed(1)} Mb`;
        if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} Kb`;
        return `${bytes} B`;
      };

      const [file, setFile] = useState<File | null>(props.file || null);
      const [isFileDropped, setIsFileDropped] = useState(false);
      const [isFileUploaded, setIsFileUploaded] = useState(false);
      const [isUploadFinished, setIsUploadFinished] = useState(false);
      const [isFileAccepted, setIsFileAccepted] = useState(false);
      const [open, setOpen] = useState(false);

      const errorInitialState = {
        hasError: false,
        message: "",
      };
      const [error, setError] = useState({
        hasError: false,
        message: "",
      });

      const onDrop = useCallback(
        (acceptedFiles: File[], fileRejections: FileRejection[]) => {
          setIsFileDropped(false);
          setIsFileUploaded(true);
          setTimeout(() => {
            setIsUploadFinished(true);
          }, 1000);

          if (acceptedFiles.length > 0) {
            setFile(acceptedFiles[0]);
            setIsFileAccepted(true);
            props.getImage(acceptedFiles[0]);
          } else if (fileRejections.length > 0) {
            setIsFileAccepted(false);
            setFile(fileRejections[0].file);

            const errors = fileRejections[0].errors;

            if (errors[0].code === "file-too-large") {
              setError({
                hasError: true,
                message: FILE_SIZE_MAX_LIMIT,
              });
            } else if (errors[0].code === "file-too-small") {
              setError({
                hasError: true,
                message:
                  props.documentType === "NATIONAL_ID_CARD"
                    ? FILE_SIZE_MIN_LIMIT_NATIONAL_ID
                    : FILE_SIZE_MIN_LIMIT,
              });
            } else if (errors[0].code === "file-invalid-type") {
              setError({
                hasError: true,
                message:
                  props.fileInvalidTypeErrorMessage ||
                  FILE_INVALID_TYPE_SMILE_ID,
              });
            }
          }
        },
        [props]
      );

      function getAcceptedFileTypes() {
        let acceptFileTypes = {};

        acceptFileTypes = { "image/jpeg": [] };

        if (props.acceptedFileTypes?.includes("png")) {
          acceptFileTypes = { ...acceptFileTypes, "image/png": [] };
        }

        if (props.acceptedFileTypes?.includes("pdf")) {
          acceptFileTypes = { ...acceptFileTypes, "application/pdf": [] };
        }
        return acceptFileTypes;
      }

      const acceptedFileTypes = getAcceptedFileTypes();

      const minSize =
        props.documentType === "NATIONAL_ID_CARD"
          ? 50000
          : props.documentType === "PROOF_OF_ADDRESS"
          ? 1
          : props.acceptedMinSize ?? MIN_SIZE_SMILE_ID;

      const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        disabled: props.disabled,
        accept: acceptedFileTypes,
        multiple: false,
        maxSize: MAX_SIZE,
        minSize: minSize,
      });

      const handlePreview = () => {
        if (file) {
          const imageUrl = URL.createObjectURL(file);
          setOpen(!open);
        }
      };

      const reset = useCallback(() => {
        setOpen(false);
        setFile(null);
        setIsFileUploaded(false);
        setIsUploadFinished(false);
      }, []);

      useEffect(() => {
        if (!props.file) {
          reset();
        }
      }, [props.parentFileChangeListener, reset, props.file]);

      useEffect(() => {
        if (props.file) {
          setIsFileAccepted(true);
          setIsUploadFinished(true);
          setIsFileUploaded(true);
          setIsFileDropped(false);
          setFile(props.file);
        }
      }, [props.file]);

      return (
        <div>
          <ImagePreviewComponent
            showModal={open}
            title={file ? file.name : ""}
            buttonText="Delete"
            showButtonIcon={true}
            onClickButton={() => {
              reset();
              setError(errorInitialState);
              props.deleteImage();
            }}
            onClickClose={() => setOpen(false)}
            image={file ? URL.createObjectURL(file) : ""}
          />
          {props.hideStatusIndicator ? (
            <div className="mb-2">
              <span className="text-sm text-neutral-700">{props.fieldName}</span>
            </div>
          ) : !isUploadFinished || !isFileAccepted ? (
            <div className="mb-2 flex items-center gap-x-1">
              <Circle fill="#9B9B9B" animate={true} />
              <span className="text-sm text-neutral-400 transition-colors">
                {props.fieldName}
              </span>
            </div>
          ) : (
            <div className="mb-2 flex items-center gap-x-1">
              <CheckCircle fill="#6EBD7F" animate={true} />
              <span className="text-sm text-accent-success transition-colors">
                {props.fieldName}
              </span>
            </div>
          )}
          {props.isUploading ? (
            <motion.div
              className={`flex ${props.dropZoneHeight ?? "h-12"} w-full items-center justify-between rounded-lg bg-neutral-25 px-4`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="flex flex-1 items-center gap-3 overflow-hidden">
                {getFileIcon(file?.name ?? "")}
                <p className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-sm text-neutral-800">
                  {file?.name}
                </p>
              </div>
              <SpinnerComponent
                type={"primary"}
                showIcon={false}
                size={20}
                strokeWidth="25"
              />
            </motion.div>
          ) : !isFileDropped && !isFileUploaded ? (
            <div
              {...getRootProps()}
              className={`flex ${props.dropZoneHeight ?? "h-20"} w-full items-center justify-center rounded-lg border-2 border-dashed ${
                props.disabled &&
                "cursor-not-allowed border-gray-100 bg-[#F9F9F9]"
              }`}
            >
              <input {...getInputProps()} />
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`text-neutral-200 ${
                  props.disabled && "text-gray-100"
                }`}
              >
                Drag and drop files here or click to upload
              </motion.p>
            </div>
          ) : isFileDropped && !isFileUploaded ? (
            <motion.div
              className={`flex ${props.dropZoneHeight ?? "h-12"} w-full items-center justify-between rounded-lg bg-neutral-25 px-4`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="flex flex-1 items-center gap-3 overflow-hidden">
                {getFileIcon(file?.name ?? "")}
                <p className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-sm text-neutral-800">
                  {file?.name}
                </p>
              </div>
              <SpinnerComponent
                type={"primary"}
                showIcon={false}
                size={20}
                strokeWidth="25"
              />
            </motion.div>
          ) : (
            <motion.div
              className={`flex ${props.dropZoneHeight ?? "h-12"} w-full items-center justify-between rounded-lg bg-neutral-25 px-4 ${
                !isFileAccepted && isUploadFinished
                  ? "border-1 border-red-500"
                  : props.hasError
                  ? "border border-[#DA291C]"
                  : ""
              }`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="flex flex-1 items-center gap-3 overflow-hidden">
                {getFileIcon(file?.name ?? "")}
                <p className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-sm text-neutral-800">
                  {file?.name}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-4">
                {!props.hideSize && (
                  <span className="text-sm text-neutral-500">
                    {formatFileSize(file?.size ?? 0)}
                  </span>
                )}
                {!props.hidePreview && isFileAccepted && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.1 }}
                    className="cursor-pointer text-neutral-600 underline"
                    onClick={handlePreview}
                  >
                    {props.previewLabel ?? "Preview"}
                  </motion.span>
                )}
                {!props.disabled && (
                  <Trash
                    animate={true}
                    className="cursor-pointer"
                    fill="#626262"
                    onClick={() => {
                      reset();
                      setError(errorInitialState);
                      props.deleteImage();
                    }}
                  />
                )}
              </div>
            </motion.div>
          )}
          {isUploadFinished && !isFileAccepted && error.hasError && (
            <div className="mt-1 ml-2 text-primary-250">
              <span className="break-words text-xs">{error.message}</span>
            </div>
          )}
        </div>
      );
    }
  );

export default DropZoneComponent;
